import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactElement, type ReactNode } from "react";
import { cloneElement } from "react";
import { Link, useLocation } from "wouter";

/**
 * A list of checkboxes, each with a bold label and an optional line under it (ALN-121: the role
 * picker and the invite form). `value` and `onChange` keep the options' order. `error` shows
 * under the list in a live region, so it's announced when it appears.
 */
export function CheckboxList({ legend, hideLegend, options, value, onChange, name, disabled, error, describedBy, className }: {
  legend: string; hideLegend?: boolean;
  options: readonly { value: string; label: string; line?: ReactNode }[];
  value: readonly string[]; onChange: (next: string[]) => void;
  name?: string; disabled?: boolean; error?: string | null; describedBy?: string; className?: string;
}) {
  const id = useId();
  const errId = `${id}-err`;
  const set = (v: string, on: boolean) => onChange(options.map(o => o.value).filter(x => (x === v ? on : value.includes(x))));
  return (
    <fieldset className={`cblist${className ? ` ${className}` : ""}`} aria-describedby={[error ? errId : null, describedBy].filter(Boolean).join(" ") || undefined}>
      <legend className={hideLegend ? "sr" : "field__label"}>{legend}</legend>
      {options.map(o => (
        <label key={o.value} className="cblist__opt">
          <input type="checkbox" name={name} value={o.value} data-value={o.value} checked={value.includes(o.value)} disabled={disabled} onChange={e => set(o.value, e.target.checked)} />
          <span className="cblist__l"><b>{o.label}</b>{o.line && <span className="small muted">{o.line}</span>}</span>
        </label>
      ))}
      <p className="small cblist__err" id={errId} aria-live="polite">{error ?? ""}</p>
    </fieldset>
  );
}

/**
 * A labelled field. Pass one input/select/textarea as the child; Field wires the label, hint and
 * error to it (id, aria-describedby, aria-invalid). `tip` (a help Tip) sits beside the label, outside it.
 * `errorId` ties an error shown elsewhere (the sign-in card's, under the fields) to this field.
 */
export function Field({ label, hint, error, errorId, tip, children, className }: { label: string; hint?: ReactNode; error?: string | null; errorId?: string; tip?: ReactNode; children: ReactElement<Record<string, unknown>>; className?: string }) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errId = error ? `${id}-err` : undefined;
  const control = cloneElement(children, {
    id: (children.props.id as string | undefined) ?? id,
    "aria-describedby": [hintId, errId, errorId].filter(Boolean).join(" ") || undefined,
    "aria-invalid": error || errorId ? true : undefined,
  });
  return (
    <div className={`field${className ? ` ${className}` : ""}`}>
      {tip ? (
        <span className="field__lt"><label className="field__label" htmlFor={(children.props.id as string | undefined) ?? id}>{label}</label>{tip}</span>
      ) : <label className="field__label" htmlFor={(children.props.id as string | undefined) ?? id}>{label}</label>}
      {control}
      {hint && <small className="field__hint" id={hintId}>{hint}</small>}
      {error && <small className="field__error" id={errId}>{error}</small>}
    </div>
  );
}

/** THE range/scope toggle: one choice of a few, with real pressed state. Not for navigation. */
export function SegmentedControl<T extends string>({ label, value, options, onChange }: { label: string; value: T; options: { value: T; label: string }[]; onChange: (v: T) => void }) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {options.map(o => <button key={o.value} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>{o.label}</button>)}
    </div>
  );
}

/**
 * Tabs whose tabs are real routes (Foundation's RouteTabs): deep links and the back button work
 * because the URL is the state. Arrow keys move between tabs.
 */
export function RouteTabs({ label, tabs, current, panelId }: { label: string; tabs: { href: string; label: string; id: string }[]; current: string; panelId: string }) {
  const [, navigate] = useLocation();
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = tabs.findIndex(t => t.id === current);
    const next = e.key === "ArrowRight" ? (i + 1) % tabs.length : e.key === "ArrowLeft" ? (i + tabs.length - 1) % tabs.length : e.key === "Home" ? 0 : e.key === "End" ? tabs.length - 1 : -1;
    if (next < 0) return;
    e.preventDefault();
    navigate(tabs[next]!.href);
    document.getElementById(`tab-${tabs[next]!.id}`)?.focus();
  };
  return (
    <div className="pagetabs" role="tablist" aria-label={label} onKeyDown={onKey}>
      {tabs.map(t => (
        <Link key={t.id} href={t.href} role="tab" id={`tab-${t.id}`} className="pagetabs__tab" aria-selected={t.id === current} aria-controls={panelId} tabIndex={t.id === current ? 0 : -1}>{t.label}</Link>
      ))}
    </div>
  );
}

/** Step header and footer for multi-step flows (onboarding, close). The body is the caller's. */
export function Wizard({ steps, current, children, onBack, onNext, nextLabel = "Continue", busy }: { steps: string[]; current: number; children: ReactNode; onBack?: () => void; onNext?: () => void; nextLabel?: string; busy?: boolean }) {
  return (
    <div className="wizard">
      <ol className="wizard__steps" aria-label="Steps">
        {steps.map((s, i) => (
          <li key={s} className="wizard__step" data-state={i < current ? "done" : undefined} aria-current={i === current ? "step" : undefined}>
            <span className="wizard__n" aria-hidden="true">{i < current ? "✓" : i + 1}</span>
            <span>{s}{i < current && <span className="sr"> (done)</span>}</span>
          </li>
        ))}
      </ol>
      {children}
      <div className="wizard__foot">
        {onBack && current > 0 ? <button type="button" className="btn" onClick={onBack}>Back</button> : <span />}
        {onNext && <button type="button" className="btn btn--primary" onClick={onNext} disabled={busy}>{nextLabel}</button>}
      </div>
    </div>
  );
}

/** A table with a labelled, keyboard-scrollable wrapper. `stack` turns rows into blocks at 390px. */
export function DataTable({ label, stack, children }: { label: string; stack?: boolean; children: ReactNode }) {
  return (
    <div className="tablewrap" role="region" aria-label={label} tabIndex={0}>
      <table className={`t${stack ? " t--stack" : ""}`}>{children}</table>
    </div>
  );
}

/** A row or card's More menu: a button that opens a short list of actions, keyboard reachable; Escape returns to the button. */
export function MoreMenu({ name, items, className }: { name: string; items: { label: string; onSelect: () => void }[]; className?: string }) {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector<HTMLButtonElement>("[role=menuitem]")?.focus();
    const away = (e: MouseEvent) => { if (!menu.current?.contains(e.target as Node) && e.target !== button.current) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const all = [...(menu.current?.querySelectorAll<HTMLButtonElement>("[role=menuitem]") ?? [])];
    const i = all.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === "Escape") { e.preventDefault(); setOpen(false); button.current?.focus(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); all[(i + 1) % all.length]?.focus(); }
    else if (e.key === "ArrowUp") { e.preventDefault(); all[(i - 1 + all.length) % all.length]?.focus(); }
    else if (e.key === "Tab") setOpen(false);
  };
  return (
    <div className={`moremenu${className ? ` ${className}` : ""}`}>
      <button ref={button} type="button" className="btn btn--sm btn--ghost" aria-haspopup="menu" aria-expanded={open} aria-controls={open ? id : undefined} onClick={() => setOpen(o => !o)}>
        More<span className="sr"> for {name}</span>
      </button>
      {open && (
        <div className="menu" id={id} role="menu" aria-label={`More for ${name}`} ref={menu} onKeyDown={onKey}>
          {items.map(i => <button key={i.label} type="button" role="menuitem" className="menu__item" onClick={() => { setOpen(false); i.onSelect(); }}>{i.label}</button>)}
        </div>
      )}
    </div>
  );
}

/**
 * A modal dialog for a decision that needs protected focus (Approve, Discard). Native <dialog>:
 * focus moves in, Tab stays inside, Escape or Cancel closes it, and focus returns to what opened it.
 * `side` turns it into a sheet on the right, for a list such as Versions.
 */
export function Dialog({ title, open, onClose, children, actions, side }: { title: string; open: boolean; onClose: () => void; children?: ReactNode; actions?: ReactNode; side?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const opener = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      opener.current = document.activeElement as HTMLElement | null;
      d.showModal();
      d.querySelector<HTMLElement>("[data-autofocus], textarea, input, select")?.focus();
    } else if (!open && d.open) {
      d.close();
      opener.current?.focus?.();
    }
  }, [open]);
  return (
    <dialog ref={ref} className={`dialog${side ? " dialog--side" : ""}`} aria-labelledby={titleId} onCancel={e => { e.preventDefault(); onClose(); }}>
      {open && (
        <>
          <div className="dialog__h">
            <h2 className="card__t" id={titleId}>{title}</h2>
            {side && <button type="button" className="iconbtn" aria-label="Close" onClick={onClose}>×</button>}
          </div>
          <div className="dialog__c">{children}</div>
          {actions && <div className="dialog__f">{actions}</div>}
        </>
      )}
    </dialog>
  );
}
